(function () {
  const initAnimations = (container) => {
    if (!container || typeof gsap === "undefined") return;

    if (
      container.dataset?.layout !== "animated_collage" ||
      container.dataset?.enableAnimation !== "true"
    )
      return;

    const cards = gsap.utils.toArray(".block-image", container);
    if (!cards.length) return;

    const baseStrength = 10;
    const decay = 0.45;

    const initialTransforms = cards.map((card) => {
      const style = window.getComputedStyle(card);
      const matrix = new DOMMatrixReadOnly(
        style.transform === "none" ? "matrix(1,0,0,1,0,0)" : style.transform
      );
      return {
        x: matrix.m41 || 0,
        y: matrix.m42 || 0,
        rotation: Math.atan2(matrix.b, matrix.a) * (180 / Math.PI) || 0,
      };
    });

    gsap.set(cards, (i) => ({
      x: initialTransforms[i].x,
      y: initialTransforms[i].y,
      rotation: initialTransforms[i].rotation,
    }));

    let rafPending = false;
    let lastEvent = null;

    const onFrame = () => {
      rafPending = false;
      if (!lastEvent) return;

      const e = lastEvent;
      lastEvent = null;

      const rect = container.getBoundingClientRect();
      const offsetX = e.clientX - rect.left - rect.width / 2;
      const offsetY = e.clientY - rect.top - rect.height / 2;

      let hoveredIndex = 0;
      let minDist = Infinity;

      cards.forEach((card, i) => {
        const r = card.getBoundingClientRect();
        const centerX = r.left + r.width / 2;
        const d = Math.abs(e.clientX - centerX);
        if (d < minDist) {
          minDist = d;
          hoveredIndex = i;
        }
      });

      cards.forEach((card, i) => {
        const base = initialTransforms[i];
        const distance = Math.abs(i - hoveredIndex);
        const decayFactor = Math.pow(decay, distance);

        const moveX = (offsetX / baseStrength) * decayFactor;
        const moveY = (offsetY / baseStrength) * decayFactor * 1.25;

        const maxMove = 240;
        const clampedX = Math.max(-maxMove, Math.min(maxMove, moveX));
        const clampedY = Math.max(-maxMove, Math.min(maxMove, moveY));

        const targetX = base.x + clampedX;
        const targetY = base.y + clampedY;

        const tiltMax = 15;
        const normalizedX = offsetX / (rect.width / 2);
        const normalizedY = offsetY / (rect.height / 2);

        const tiltX = normalizedY * tiltMax * decayFactor;
        const tiltY = -normalizedX * tiltMax * decayFactor;

        gsap.to(card, {
          x: targetX,
          y: targetY,
          rotationX: tiltX,
          rotationY: tiltY,
          rotation: base.rotation + tiltY * 0.3,
          duration: 0.38,
          ease: "power2.out",
          overwrite: true,
        });
      });
    };

    const handleMouseMove = (e) => {
      lastEvent = e;
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(onFrame);
      }
    };

    const handleMouseLeave = () => {
      lastEvent = null;
      rafPending = false;

      cards.forEach((card, i) => {
        const base = initialTransforms[i];
        gsap.to(card, {
          x: base.x,
          y: base.y,
          rotationX: 0,
          rotationY: 0,
          rotation: base.rotation,
          duration: 0.8,
          ease: "elastic.out(1, 0.6)",
          overwrite: true,
        });
      });
    };

    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);

      gsap.killTweensOf(cards);
      cards.forEach((card) => card.removeAttribute("style"));
    };
  };

  const initSideFadeCarousel = (container) => {
    if (!container || container.dataset?.layout !== "side_fade") return;

    const cards = Array.from(container.querySelectorAll(".block-image"));
    if (cards.length < 2) return;

    const wrapper = container.closest(
      ".side-fade-carousel, .animated-image-group-responsive"
    );
    const prevBtn = wrapper?.querySelector('[data-direction="prev"]');
    const nextBtn = wrapper?.querySelector('[data-direction="next"]');

    const count = cards.length;
    const maxSlot = Math.floor((count - 1) / 2);
    const minSlot = -Math.floor(count / 2);

    const assignInitialSlots = () => {
      const center = Math.floor(count / 2);
      cards.forEach((card, index) => {
        card.dataset.slot = String(index - center);
      });
    };

    assignInitialSlots();

    const rotate = (direction) => {
      cards.forEach((card) => {
        const current = parseInt(card.dataset.slot, 10) || 0;
        let next = current + direction;

        if (next > maxSlot) {
          const teleportSlot = minSlot - 1;
          card.classList.add("is-teleporting");
          card.dataset.slot = String(teleportSlot);
          requestAnimationFrame(() => {
            card.getBoundingClientRect();
            card.classList.remove("is-teleporting");
            requestAnimationFrame(() => {
              card.dataset.slot = String(minSlot);
            });
          });
        } else if (next < minSlot) {
          const teleportSlot = maxSlot + 1;
          card.classList.add("is-teleporting");
          card.dataset.slot = String(teleportSlot);
          requestAnimationFrame(() => {
            card.getBoundingClientRect();
            card.classList.remove("is-teleporting");
            requestAnimationFrame(() => {
              card.dataset.slot = String(maxSlot);
            });
          });
        } else {
          card.dataset.slot = String(next);
        }
      });
    };

    const goNext = () => rotate(-1);
    const goPrev = () => rotate(1);

    const DRAG_THRESHOLD = 40;
    let isDragging = false;
    let startX = 0;
    let deltaX = 0;
    let lastRotationTime = 0;

    const endDrag = () => {
      if (!isDragging) return;
      isDragging = false;
      deltaX = 0;
      container.classList.remove("is-dragging");
    };

    const handlePointerDown = (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      isDragging = true;
      startX = event.clientX;
      deltaX = 0;
      container.classList.add("is-dragging");
      container.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event) => {
      if (!isDragging) return;
      deltaX = event.clientX - startX;

      if (Math.abs(deltaX) >= DRAG_THRESHOLD) {
        const now = performance.now();
        if (now - lastRotationTime > 180) {
          lastRotationTime = now;
          if (deltaX < 0) {
            goNext();
          } else {
            goPrev();
          }
          startX = event.clientX;
          deltaX = 0;
        }
      }
    };

    const handlePointerUp = (event) => {
      if (container.hasPointerCapture(event.pointerId)) {
        container.releasePointerCapture(event.pointerId);
      }
      endDrag();
    };

    const handleWheel = (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();

      const now = performance.now();
      if (now - lastRotationTime < 220) return;
      lastRotationTime = now;

      if (event.deltaX > 0) {
        goNext();
      } else {
        goPrev();
      }
    };

    const handlePrev = () => goPrev();
    const handleNext = () => goNext();

    container.addEventListener("pointerdown", handlePointerDown);
    container.addEventListener("pointermove", handlePointerMove);
    container.addEventListener("pointerup", handlePointerUp);
    container.addEventListener("pointercancel", handlePointerUp);
    container.addEventListener("pointerleave", endDrag);
    container.addEventListener("wheel", handleWheel, { passive: false });
    prevBtn?.addEventListener("click", handlePrev);
    nextBtn?.addEventListener("click", handleNext);

    return () => {
      container.removeEventListener("pointerdown", handlePointerDown);
      container.removeEventListener("pointermove", handlePointerMove);
      container.removeEventListener("pointerup", handlePointerUp);
      container.removeEventListener("pointercancel", handlePointerUp);
      container.removeEventListener("pointerleave", endDrag);
      container.removeEventListener("wheel", handleWheel);
      prevBtn?.removeEventListener("click", handlePrev);
      nextBtn?.removeEventListener("click", handleNext);
      container.classList.remove("is-dragging");
      cards.forEach((card) => {
        card.classList.remove("is-teleporting");
        delete card.dataset.slot;
      });
    };
  };

  const setupResponsiveAnimations = (section) => {
    const wrapper = section.querySelector(".animated-image-group-responsive");
    const container = section.querySelector(".block-animated-image-group");
    let cleanupCollage = null;
    let cleanupSideFade = null;

    const cleanupActiveSideFade = () => {
      if (!cleanupSideFade) return;
      cleanupSideFade();
      cleanupSideFade = null;
    };

    const cleanupActiveCollage = () => {
      if (!cleanupCollage) return;
      cleanupCollage();
      cleanupCollage = null;
    };

    const sync = () => {
      if (!container) return;

      const width = section.getBoundingClientRect().width;
      const isWide = width >= 750;
      const hasPointer = window.matchMedia("(pointer: fine)").matches;
      const desktopLayout = container.dataset?.desktopLayout || container.dataset?.layout;
      const mobileLayout = container.dataset?.mobileLayout || desktopLayout;
      const activeLayout = isWide ? desktopLayout : mobileLayout;
      const enableAnimation =
        isWide && container.dataset?.desktopEnableAnimation === "true";

      if (container.dataset.layout !== activeLayout) {
        cleanupActiveSideFade();
        cleanupActiveCollage();
        container.dataset.layout = activeLayout;
      }

      container.dataset.enableAnimation = enableAnimation ? "true" : "false";
      if (wrapper) {
        wrapper.dataset.activeLayout = activeLayout;
      }

      if (activeLayout === "side_fade") {
        cleanupActiveCollage();
        if (!cleanupSideFade) {
          cleanupSideFade = initSideFadeCarousel(container);
        }
        return;
      }

      cleanupActiveSideFade();

      if (
        activeLayout === "animated_collage" &&
        enableAnimation &&
        hasPointer
      ) {
        if (!cleanupCollage) {
          cleanupCollage = initAnimations(container);
        }
      } else {
        cleanupActiveCollage();
      }
    };

    const observer = new ResizeObserver(() => sync());
    observer.observe(section);
    sync();

    return () => {
      cleanupActiveCollage();
      cleanupActiveSideFade();
      observer.disconnect();
    };
  };

  let teardown = null;
  let managedSection = null;

  const parentSection = document.currentScript.parentElement;
  if (
    parentSection &&
    parentSection.querySelector(".block-animated-image-group")
  ) {
    managedSection = parentSection;
    teardown = setupResponsiveAnimations(parentSection);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const target = event.target;

    if (!managedSection || target !== managedSection) return;
    if (!target.querySelector(".block-animated-image-group")) return;

    if (teardown) teardown();

    managedSection = target;
    teardown = setupResponsiveAnimations(target);
  });
})();
